/**
 * Mathematical Measuring Ruler with precision millimeter and centimeter graduations.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface RulerProps {
  length?: number;
  width?: number;
  cmMarks?: number; // total cm
  opacity?: number;
  theme?: 'wood' | 'clear' | 'steel';
}

export class Ruler extends SVGObject {
  public props: RulerProps;

  constructor(config: SVGObjectConfig & { props?: RulerProps }) {
    super({ ...config, type: 'ruler', layer: 'foreground' });
    this.props = {
      length: 500,
      width: 50,
      cmMarks: 15,
      theme: 'clear',
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const len = this.props.length || 500;
    const w = this.props.width || 50;
    const totalCm = this.props.cmMarks || 15;
    const pxPerCm = len / totalCm;

    // Body
    const body = Renderer.createElement('rect');
    Renderer.setAttributes(body, {
      x: 0,
      y: 0,
      width: len,
      height: w,
      rx: 4,
      ry: 4,
      fill: this.props.theme === 'wood' ? '#d97706' : 'rgba(241, 245, 249, 0.85)',
      stroke: '#94a3b8',
      'stroke-width': 1.5,
      filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.25))'
    });
    this.container.appendChild(body);

    // Ticks & Numbers
    const marksGroup = Renderer.createElement('g');
    for (let cm = 0; cm <= totalCm; cm++) {
      const cmX = cm * pxPerCm;

      // 10 mm ticks per cm
      for (let mm = 0; mm < 10; mm++) {
        if (cm === totalCm && mm > 0) break;
        const mmX = cmX + (mm * pxPerCm) / 10;
        let tickH = 6;
        if (mm === 0) tickH = 18;
        else if (mm === 5) tickH = 12;

        const line = Renderer.createElement('line');
        Renderer.setAttributes(line, {
          x1: mmX,
          y1: 0,
          x2: mmX,
          y2: tickH,
          stroke: '#0f172a',
          'stroke-width': mm === 0 ? 1.5 : 0.8
        });
        marksGroup.appendChild(line);
      }

      // Centimeter digit
      if (cm > 0 && cm < totalCm) {
        const text = Renderer.createElement('text');
        Renderer.setAttributes(text, {
          x: cmX,
          y: 28,
          'font-size': 11,
          'font-family': 'Inter, sans-serif',
          'font-weight': '600',
          fill: '#0f172a',
          'text-anchor': 'middle'
        });
        text.textContent = String(cm);
        marksGroup.appendChild(text);
      }
    }

    this.container.appendChild(marksGroup);
  }
}
